import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const DateInputScreen = () => {
  const navigate = useNavigate();
  const [lastPeriodDate, setLastPeriodDate] = useState('');
  const [cycleLength, setCycleLength] = useState(28);
  const [trackingMode, setTrackingMode] = useState('cycle'); // 'cycle' or 'moon'

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (trackingMode === 'cycle' && !lastPeriodDate) return;

    // Store the data and navigate to home
    localStorage.setItem('trackingMode', trackingMode);
    
    if (trackingMode === 'cycle') {
      localStorage.setItem('lastPeriodDate', lastPeriodDate);
      localStorage.setItem('cycleLength', cycleLength.toString());
    } else {
      // Clear any existing cycle data
      localStorage.removeItem('lastPeriodDate');
      localStorage.removeItem('cycleLength');
    }
    
    navigate('/');
  };

  const handleSkip = () => {
    // Set default values and navigate
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() - 14); // 14 days ago as default
    localStorage.setItem('trackingMode', 'cycle');
    localStorage.setItem('lastPeriodDate', defaultDate.toISOString().split('T')[0]);
    localStorage.setItem('cycleLength', '28');
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-white px-6 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-md mx-auto"
      >
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="italiana-font text-3xl text-black mb-4 leading-tight">
            Let's Personalize Your Natural Rhythm 🌙
          </h1>
          <p className="text-sm text-gray-600 leading-relaxed">
            Choose how you'd like to sync with your natural cycles for personalized guidance
          </p>
        </div>

        {/* Tracking Mode Selection */}
        <div className="mb-8">
          <h3 className="text-sm font-medium text-black mb-4">
            How would you like to track your natural rhythm?
          </h3>
          
          <div className="space-y-3">
            <motion.button
              type="button"
              onClick={() => setTrackingMode('cycle')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`w-full p-4 text-left border rounded-lg transition-all ${
                trackingMode === 'cycle' 
                  ? 'border-gold bg-gold bg-opacity-10' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="flex items-start space-x-3">
                <div className={`w-4 h-4 rounded-full border-2 mt-1 ${
                  trackingMode === 'cycle' ? 'border-gold bg-gold' : 'border-gray-300'
                }`} />
                <div>
                  <h4 className="font-medium text-black">🩸 Menstrual Cycle + Moon Phases</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Track your period to get precise, personalized guidance based on your exact cycle day
                  </p>
                </div>
              </div>
            </motion.button>

            <motion.button
              type="button"
              onClick={() => setTrackingMode('moon')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`w-full p-4 text-left border rounded-lg transition-all ${
                trackingMode === 'moon' 
                  ? 'border-gold bg-gold bg-opacity-10' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="flex items-start space-x-3">
                <div className={`w-4 h-4 rounded-full border-2 mt-1 ${
                  trackingMode === 'moon' ? 'border-gold bg-gold' : 'border-gray-300'
                }`} />
                <div>
                  <h4 className="font-medium text-black">🌙 Moon Phases Only</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Perfect if you don't menstruate, have irregular cycles, or prefer lunar guidance
                  </p>
                </div>
              </div>
            </motion.button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {trackingMode === 'cycle' && (
            <>
              <div>
                <label className="block text-sm font-medium text-black mb-3">
                  When did your last period start?
                </label>
                <input
                  type="date"
                  value={lastPeriodDate}
                  onChange={(e) => setLastPeriodDate(e.target.value)}
                  className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all"
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-3">
                  What's your typical cycle length?
                </label>
                <select
                  value={cycleLength}
                  onChange={(e) => setCycleLength(parseInt(e.target.value))}
                  className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all"
                >
                  {[21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35].map(length => (
                    <option key={length} value={length}>
                      {length} days
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {trackingMode === 'moon' && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-medium text-black mb-2">🌙 Moon Phase Guidance</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                You'll receive personalized actions based on the current moon phase and its natural energy patterns. Perfect for connecting with lunar rhythms and seasonal cycles.
              </p>
            </div>
          )}

          <div className="space-y-4 pt-6">
            <motion.button
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={trackingMode === 'cycle' && !lastPeriodDate}
              className="w-full gold-bg text-white py-4 px-6 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {trackingMode === 'cycle' 
                ? 'Get My Personalized Phase →' 
                : 'Start Moon Phase Guidance →'
              }
            </motion.button>

            <button
              type="button"
              onClick={handleSkip}
              className="w-full text-gray-500 py-2 text-sm hover:text-black transition-colors"
            >
              Skip for now (use demo phase)
            </button>
          </div>
        </form>

        {/* Privacy Note */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500">
            🔒 Your data stays private on your device
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default DateInputScreen;